import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, InputBase, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, 
  TableRow, TextField, Typography } from '@mui/material';
import { Search, RefreshCw } from 'lucide-react';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import * as XLSX from 'xlsx';

const useStyles = {
  evenRow: {
    backgroundColor: "#C1E0FB",
  },
  oddRow: {
    backgroundColor: "#FFFFFF",
  },
  PayInText: {
    color: "#027BFE",
  },
};

const TotalPayinReport = () => {
  const [data, setData] = useState([]);
  const [TotalPayin, setTotalPayin] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [todayTransacted, setTodayTransacted] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [failedAmount, setFailedAmount] = useState(0);
  const [refundAmount, setRefundAmount] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTransactionId, setSearchTransactionId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [noContent, setNoContent] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);


  const handleSearch = () => {
    setPage(0);
    if (!searchQuery.trim()) {
      fetchCompleteData(0, fromDate, toDate, "", 10);
    } else {
      fetchCompleteData(0, fromDate, toDate, searchQuery, rowsPerPage);
    }
  };

  const handleSearchInput = (event) => {
    const updatedSearchQuery = event.target.value;
    setSearchQuery(updatedSearchQuery);

    if (updatedSearchQuery.length === 0) {
      fetchCompleteData(0, "", "", "", rowsPerPage);
    }
  };

  const fetchCompleteData = async (
    page,
    fromDate,
    toDate,
    searchQuery,
    rowsPerPage,
    isExport = false
  ) => {
    setLoading(true);
    const token = sessionStorage.getItem("jwtToken");
    let url = "https://api.ipaisa.co.in/myapp/admin/allPayIn?";

    if (searchQuery.trim()) {
      url += `searchBy=${searchQuery}&`;
    }
    if (fromDate) {
      url += `fromDate=${fromDate}&`;
    }
    if (toDate) {
      url += `toDate=${toDate}&`;
    }
    if (isExport) {
      url += `export=true&`;
    }
    url += `page=${page}&size=${isExport ? 1000 : rowsPerPage}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (
        response.data.statusCodeValue === 204 ||
        response.data.body.message === "NO_CONTENT"
      ) {
        setFilteredData([]);
        setTotalTransactions(0);
        setPendingAmount(0);
        setFailedAmount(0);
        setRefundAmount(0);
        return [];
      } else {
        const body = response.data.body;
        if (!isExport) {
          setFilteredData(body.data || []);
          setTotalTransactions(body.totalRecords);
          setTotalPayin(body.amountSummary?.successAmount || 0);
          setPendingAmount(body.amountSummary.pendingAmount || 0);
          setFailedAmount(body.amountSummary.failedAmount || 0);
          setRefundAmount(body.amountSummary.refundAmount || 0);
          const last7DaysData = getLast7DaysData(body.Transactions || []);
          setData(last7DaysData);
        }
        return body.data || [];
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!isExport) {
        setFilteredData([]);
        setTotalTransactions(0);
        setPendingAmount(0);
        setFailedAmount(0);
        setRefundAmount(0);
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchExportCompleteData = async (
    fromDate,
    toDate,
    searchQuery,
    isExport = false
  ) => {
    setLoading(true);
    const token = sessionStorage.getItem("jwtToken");
    let url = "https://api.ipaisa.co.in/myapp/admin/allPayInToExport?";

    if (searchQuery.trim()) {
      url += `searchBy=${searchQuery}&`;
    }
    if (fromDate) {
      url += `fromDate=${fromDate}&`;
    }
    if (toDate) {
      url += `toDate=${toDate}&`;
    }
    
    

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (
        response.data.statusCodeValue === 204 ||
        response.data.body.message === "NO_CONTENT"
      ) {
        setFilteredData([]);
        setTotalTransactions(0);
        setPendingAmount(0);
        setFailedAmount(0);
        setRefundAmount(0);
        return [];
      } else {
        const body = response.data.body;
        if (!isExport) {
          setFilteredData(body.data || []);
          setTotalTransactions(body.totalRecords);
          setTotalPayin(body.Successful_Transactions_Amount || 0);
          setPendingAmount(body.Pending_Transactions_Amount || 0);
          setFailedAmount(body.Failed_Transactions_Amount || 0);
          setRefundAmount(body.Refund_Transactions_Amount || 0);
          const last7DaysData = getLast7DaysData(body.Transactions || []);
          setData(last7DaysData);
        }
        return body.data || [];
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!isExport) {
        setFilteredData([]);
        setTotalTransactions(0);
        setPendingAmount(0);
        setFailedAmount(0);
        setRefundAmount(0);
      }
      return [];
    } finally {
      setLoading(false);
    }
  };





  const handleDownloadCSV = async () => {
    setIsDownloadingCSV(true);
    try {
      const exportData = await fetchExportCompleteData(fromDate, toDate, searchQuery, true);

      if (!exportData || exportData.length === 0) {
        toast.info('No data available to download');
        return;
      }
      
      // Prepare data for Excel
      const excelData = exportData.map((row, index) => ({
        'Sr. No.': index + 1,
        'Txn ID': row.txnid,
        'User Name': row.userName,
        'User ID': row.userId,
        'Date': formatDate(row.addedon),
        'PayIn Amount': Number(row.netAmountDebit),
        'Amount': Number(row.amount),
        'Settlement': row.settlement,
        'Status': row.status
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const wscols = [
        {wch: 6},  // Sr. No.
        {wch: 15}, // Txn ID
        {wch: 20}, // User Name
        {wch: 15}, // User ID
        {wch: 20}, // Date
        {wch: 15}, // PayIn Amount
        {wch: 12}, // Amount
        {wch: 15}, // Settlement
        {wch: 15}  // Status
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "PayIn Transactions");

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `payin-transactions-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Failed to download Excel. Please try again.');
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloadingPDF(true);
    try {
      const exportData = await fetchExportCompleteData(fromDate, toDate, searchQuery, true);

      if (!exportData || exportData.length === 0) {
        toast.info('No data available to download');
        return;
      }
      
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Add header
      doc.setFontSize(20);
      doc.setTextColor(2, 123, 254); // Blue color
      doc.text('Ipaisa PayIn Transactions Report', 14, 20);
      
      // Add date range if available
      if (fromDate && toDate) {
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 30);
      }
      
      // Add generation timestamp
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 40);
      
      // Add summary information
      doc.setFontSize(12);
      doc.text(`Total Transactions: ${totalTransactions}`, 14, 50);
      doc.text(`Total Amount: ₹${Number(TotalPayin).toFixed(2)}`, 14, 60);
      
      const tableColumn = [
        '#', 'Txn ID', 'User Name', 'User ID', 'Date', 
        'PayIn Amount', 'Amount', 'Settlement', 'Status'
      ];
      
      const tableRows = exportData.map((row, index) => [
        index + 1,
        row.txnid,
        row.userName,
        row.userId,
        formatDate(row.addedon),
        `₹${Number(row.netAmountDebit).toFixed(2)}`,
        `₹${Number(row.amount).toFixed(2)}`,
        row.settlement,
        row.status
      ]);
      
      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 70,
        theme: 'grid',
        styles: { 
          fontSize: 8, 
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headerStyles: { 
          fillColor: [2, 123, 254],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        columnStyles: {
          0: { cellWidth: 10 },  // Sr. No.
          1: { cellWidth: 25 },  // Txn ID
          2: { cellWidth: 30 },  // User Name
          3: { cellWidth: 20 },  // User ID
          4: { cellWidth: 30 },  // Date
          5: { cellWidth: 20 },  // PayIn Amount
          6: { cellWidth: 15 },  // Amount
          7: { cellWidth: 20 },  // Settlement
          8: { cellWidth: 25 }   // Status
        },
        margin: { top: 70 }
      });
      
      doc.save(`payin-transactions-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  useEffect(() => {
    fetchCompleteData(page, fromDate, toDate, searchQuery, rowsPerPage);
  }, [page]);

  useEffect(() => {
    // Function to format date as YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    // Calculate dates
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
  
    const formattedFromDate = formatDate(sevenDaysAgo);
    const formattedToDate = formatDate(today);
  
    // Set states and use the formatted dates directly
    setFromDate(formattedFromDate);
    setToDate(formattedToDate);
  
    console.log("From Date: ", formattedFromDate);
    console.log("To Date: ", formattedToDate);
  }, []); 

  const handleClear = () => {
   
    setSearchQuery("");
  
    setPage(0);
    fetchCompleteData(0, fromDate, toDate, "", rowsPerPage);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchCompleteData(page, fromDate, toDate, searchQuery, rowsPerPage);
    setIsRefreshing(false);
  };

  const getLast7DaysData = (transactions) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const today = new Date();
    const last7Days = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = days[date.getDay()];
      const dayDate = date.toISOString().split("T")[0];

      const payin = transactions
        .filter((txn) => txn.timestamp?.split("T")[0] === dayDate)
        .reduce((sum, txn) => sum + Number(txn.txnValue || 0), 0);

      last7Days.push({ day: dayName, payin });
    }
    return last7Days;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchCompleteData(0, fromDate, toDate, searchQuery, newRowsPerPage);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Paper style={{ padding: "2rem", borderRadius: "10px", boxShadow: "none" }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between" style={{ flexWrap: "wrap" }}>
          {/* Summary Cards */}
          <Grid item xs={12} sm={6} md={2} sx={{
            border: "1px solid #D9D9D9",
            borderRadius: "18px",
            "&:hover": { boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" },
          }}>
            <Typography style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold" }}>
              Total Transactions
            </Typography>
            <Typography style={{ textAlign: "center" }}>
              {totalTransactions}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "black" }}>
              Successful Amount
            </Typography>
            <Typography style={{ textAlign: "center", color: "green" }}>
              ₹{Number(TotalPayin).toFixed(2)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "black" }}>
              Pending Amount
            </Typography>
            <Typography style={{ textAlign: "center", color: "orange" }}>
              ₹{Number(pendingAmount).toFixed(2)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "black" }}>
              Failed Amount
            </Typography>
            <Typography style={{ textAlign: "center", color: "red" }}>
              ₹{Number(failedAmount).toFixed(2)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "black" }}>
              Refund Amount
            </Typography>
            <Typography style={{ textAlign: "center", color: "darkgoldenrod" }}>
              ₹{Number(refundAmount).toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container style={{ display: "flex", justifyContent: "space-between", gap: "0.5%", marginTop: "2%" }}>
        <Grid item xs={12} md={4}>
          <Paper style={{ boxShadow: "none", padding: "1rem", borderRadius: "10px", marginBottom: "4%" }}>
            <Typography variant="h6" gutterBottom>
              7-Day Payin Summary
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: "1rem" }}>
              Total Payin: ₹{Number(TotalPayin).toFixed(2)}
            </Typography>
          </Paper>

          <Paper style={{ boxShadow: "none", padding: "1rem", borderRadius: "10px", marginBottom: "4%" }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              style={{ marginBottom: "0.5rem" }}
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              style={{ marginBottom: "1rem" }}
            />
            <Button variant="contained" color="primary" onClick={handleSearch} style={{ marginRight: "0.5rem" }}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Grid container alignItems="center" justifyContent="space-between" style={{ marginTop: "2%" }}>
        <Grid item xs={12} sm={4} style={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6">Recent Transactions</Typography>
          <IconButton onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? <CircularProgress size={24} /> : <RefreshCw size={20} />}
          </IconButton>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box sx={{
            display: "flex",
            flex: 1,
            borderRadius: "10px",
            border: "1px solid rgba(0, 0, 0, 0.3)",
            padding: "0.5rem",
          }}>
            <InputBase
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchInput}
            />
            <IconButton color="primary" onClick={handleSearch}>
              <Search size={20} />
            </IconButton>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleDownloadCSV}
            disabled={isDownloadingCSV}
            sx={{ 
              display: 'flex', 
              gap: 1,
              backgroundColor: '#22c55e',
              '&:hover': { backgroundColor: '#16a34a' }
            }}
          >
            {isDownloadingCSV ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <span>Download Excel</span>
              </>
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleDownloadPDF}
            disabled={isDownloadingPDF}
            sx={{ 
              display: 'flex', 
              gap: 1,
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' }
            }}
          >
            {isDownloadingPDF ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <span>Download PDF</span>
              </>
            )}
          </Button>
        </Grid>
      </Grid>

      <Grid style={{ marginTop: "2%", marginBottom: "5%" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "12px 12px 0px 0px", boxShadow: "none" }}>
            <Table>
              <TableHead style={{ backgroundColor: "#027BFE", color: "white" }}>
                <TableRow>
                  <TableCell style={{ color: "white" }}>Sr. No.</TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>Txn ID</TableCell>
                  <TableCell style={{ color: "white" }}>User Name</TableCell>
                  <TableCell style={{ color: "white" }}>User ID</TableCell>
                  <TableCell style={{ color: "white" }}>Date</TableCell>
                  <TableCell style={{ color: "white" }}>PayIn Amount</TableCell>
                  <TableCell style={{ color: "white" }}>Amount</TableCell>
                  <TableCell style={{ color: "white" }}>Settlement</TableCell>
                  <TableCell style={{ color: "white" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <TableRow key={index} sx={index % 2 === 0 ? useStyles.oddRow : useStyles.evenRow}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="center">{row.txnid}</TableCell>
                      <TableCell>{row.userName}</TableCell>
                      <TableCell>{row.userId}</TableCell>
                      <TableCell>{formatDate(row.addedon)}</TableCell>
                      <TableCell>₹{Number(row.netAmountDebit).toFixed(2)}</TableCell>
                      <TableCell>₹{Number(row.amount).toFixed(2)}</TableCell>
                      <TableCell>{row.settlement}</TableCell>
                      <TableCell 
                        sx={{
                          color: row.status === 'success' || row.status === 'SUCCESS' ? 'green' :
                                 row.status === 'failure' || row.status === 'Failed' ? '#dc2626' :
                                 row.status === 'pending' || row.status === 'Pending' ? '#f59e0b' :
                                 row.status === 'refund' ? '#6366f1' :
                                 row.status === 'preinitiated' ? '#6366f1' :
                                 row.status === 'userCancelled' ? '#dc2626' : '#6366f1',
                          fontWeight: 'bold'
                        }}
                      >
                        {row.status}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: "center" }}>
                      No transactions available for the selected date range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalTransactions}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              nextIconButtonProps={{ disabled: !hasMore }}
            />
          </TableContainer>
        )}
      </Grid>
      <ToastContainer
                          position="top-right"
                          autoClose={3000}
                          hideProgressBar={false}
                          newestOnTop={true}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss={false}
                          draggable
                          pauseOnHover
                          theme="colored"
                        />
    </Box>
  );
};

export default TotalPayinReport;